import { OptimisticEventVerifier } from "applesauce-core";
import { isEvent, setVerifyWrappedEventMethod } from "applesauce-core/helpers";
import { initNostrWasm, type Nostr } from "nostr-wasm";
import { BehaviorSubject } from "rxjs";
import { optimisticVerification } from "./settings";
import {
  verifiedSymbol,
  verifyEvent as verifyJavaScriptEvent,
  type NostrEvent,
  type VerifiedEvent,
} from "nostr-tools/pure";

let wasm: Nostr | undefined;

/** Full verification for trust-bearing events, tombstones, and wrapped events. */
export function verifyEvent(event: NostrEvent): event is VerifiedEvent {
  if (!isEvent(event)) return false;
  if (event[verifiedSymbol] === true) return true;
  if (!wasm) return verifyJavaScriptEvent(event);
  try {
    wasm.verifyEvent(event);
    Object.defineProperty(event, verifiedSymbol, {
      value: true,
      configurable: true,
    });
    return true;
  } catch {
    return false;
  }
}

// Embedded WASM avoids a deployment-specific fetch URL (including on Android).
// Ingestion remains fully checked by the JS fallback while WASM initializes.
export const eventVerificationReady = initNostrWasm()
  .then((instance) => {
    wasm = instance;
  })
  .catch((error: unknown) => {
    console.warn(
      "WASM event verification unavailable; using JavaScript:",
      error,
    );
  });

// Relay trust evidence lives under our own prefix so it can be reset without
// relying on the upstream storage key layout.
const RELAY_TRUST_PREFIX = "relayTrust:";

function createOptimisticVerifier(): OptimisticEventVerifier {
  // A reset replaces the verifier; writes still queued by the old instance
  // must not restore the evidence that was just cleared.
  const current = () => optimisticVerifier$.value === verifier;
  const verifier = new OptimisticEventVerifier({
    verifyEvent,
    storage: {
      getItem: (key) => localStorage.getItem(RELAY_TRUST_PREFIX + key),
      setItem: (key, value) => {
        if (current()) localStorage.setItem(RELAY_TRUST_PREFIX + key, value);
      },
    },
  });
  return verifier;
}

export const optimisticVerifier$ = new BehaviorSubject(
  createOptimisticVerifier(),
);

/** Forgets all earned relay trust so every relay starts at full verification. */
export function resetRelayTrust(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(RELAY_TRUST_PREFIX)) localStorage.removeItem(key);
  }
  optimisticVerifier$.next(createOptimisticVerifier());
}

// Limit sampling to conversational content. Everything else, including all
// repository roots, role/state/status events, user lists and payments, needs a
// checked signature before it can influence authorization or a mutation.
const sampledKinds = new Set([1, 7, 1111]);
export function verifyStoreEvent(event: NostrEvent): boolean {
  if (!sampledKinds.has(event.kind) || !optimisticVerification.value)
    return verifyEvent(event);
  return optimisticVerifier$.value.verifyEvent(event);
}

setVerifyWrappedEventMethod(verifyEvent);
